import * as React from "react"

import { Check } from "lucide-react"

import { cn } from "../../cn"

export interface ChoiceCardProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "title" | "onSelect"
> {
  /** Primary text. */
  title: React.ReactNode
  /** Secondary line under the title. */
  description?: React.ReactNode
  /** Rendered inline after the title, e.g. a Badge. */
  badge?: React.ReactNode
  /** Extra content under the description, e.g. more badges or a summary line. */
  children?: React.ReactNode
  selected: boolean
  onSelect: () => void
  /**
   * A control rendered at the card's right edge, outside the select button so
   * the two never nest. Typically a preview or info button.
   */
  trailing?: React.ReactNode
  /** Class applied to the outer wrapper rather than the button. */
  wrapperClassName?: string
  /**
   * "card" carries the selected and hover states a list of candidates needs.
   * "plain" is the single already-made choice shown inside a section that is
   * itself a card: the divider-coloured outline stays, the fill does not.
   */
  variant?: "card" | "plain"
}

/**
 * One option in a picker: the whole card is the select control, with a check
 * on the selected one. Used wherever a choice has enough to say that a radio
 * label would not fit, such as voices and audio presets.
 */
const ChoiceCard = React.forwardRef<HTMLButtonElement, ChoiceCardProps>(
  (
    {
      title,
      description,
      badge,
      children,
      selected,
      onSelect,
      trailing,
      disabled,
      className,
      wrapperClassName,
      variant = "card",
      ...props
    },
    ref,
  ) => (
    <div className={cn("relative", wrapperClassName)}>
      <button
        ref={ref}
        type="button"
        aria-pressed={selected}
        disabled={disabled}
        onClick={onSelect}
        className={cn(
          "flex min-h-14 w-full flex-col justify-center gap-1 rounded-md p-3 text-left transition-colors",
          trailing && "pr-14",
          "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-coval-border-active",
          // Only the chosen card is filled, so the selection is what the eye
          // lands on rather than every candidate reading as a raised box.
          variant === "card" &&
            (selected
              ? "border border-coval-border-active bg-surface-0"
              : "border border-coval-border hover:border-coval-border-active hover:bg-surface-default-hover"),
          variant === "plain" &&
            "border border-coval-border hover:bg-surface-default-hover",
          disabled && "opacity-60",
          className,
        )}
        {...props}
      >
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-sm font-semibold text-coval-text">
            {title}
          </span>
          {badge}
          {selected && (
            <Check className="size-4 shrink-0 text-coval-contrast" />
          )}
        </div>
        {description && (
          <p className="text-xs text-coval-text-muted">{description}</p>
        )}
        {children}
      </button>
      {trailing && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {trailing}
        </div>
      )}
    </div>
  ),
)

ChoiceCard.displayName = "ChoiceCard"

export { ChoiceCard }
